export type ViewMode = 'grid' | 'list';
export type Theme = 'dark' | 'light';

/* Reviews sit last before contacts on purpose: social proof works hardest
   next to the decision, not next to the work. */
export const sectionIds = [
  'projects',
  'stack',
  'blog',
  'about',
  'testimonials',
  'contact',
] as const;
export type SectionId = (typeof sectionIds)[number];

/** Russian plural: [1 проект, 2 проекта, 5 проектов]. */
export type PluralForms = [string, string, string];

export type NavItem = {
  id: SectionId;
  label: string;
};

export type SocialId = 'github' | 'telegram' | 'kwork';

export type Social = {
  id: SocialId;
  label: string;
  href: string;
  /** Handle shown under the label on the contacts page ("@nickname"). */
  handle: string;
};

export type SectionHeading = {
  title: string;
  /** Rendered as separate lines under the title. */
  subtitle: string[];
};

export type Project = {
  id: string;
  /** Order badge shown above the title ("01", "02", ...) */
  number: string;
  title: string;
  subtitle: string;
  stack: string[];
  /** Preview screenshot. Drop a file into /public/previews and point here. */
  preview: string | null;
  /** How many of the 12 grid columns the card takes on desktop. */
  span: number;
  /**
   * Powers the expanded case study. A project without `details` stays a plain
   * card: no pointer cursor, no open button, nothing that promises a click.
   */
  details?: ProjectDetails;
};

/**
 * `kind` is what makes a link worth more than a link: a running demo and a
 * readable repository are the two things a visitor can check without asking
 * anyone, so the card advertises which of them a project has.
 */
export type ProjectLinkKind = 'demo' | 'repo';

export type ProjectLink = {
  label: string;
  href: string;
  kind: ProjectLinkKind;
};

/** A screenshot shown inside the case study. */
export type ProjectShot = {
  src: string;
  /** What the screen shows — a visitor with images off still learns something. */
  alt: string;
};

export type ProjectDetails = {
  overview: string;
  highlights: string[];
  year?: string;
  role?: string;
  /**
   * Real screens of the project, in the order a visitor should meet them:
   * the public page first, the working interface second.
   */
  shots?: ProjectShot[];
  /**
   * Still being built. Renders a badge on the card, so a visitor scanning the
   * grid is told before they wonder why this one has nothing to open —
   * "in progress" is a fact about the work, not an excuse for the absence.
   */
  wip?: boolean;
  /** Repository / live demo. Empty until there is a real URL to point at. */
  links?: ProjectLink[];
  /**
   * Why this project has no link, when it has none for a reason worth saying
   * out loud — client-owned code, a private repository. A missing link that
   * is explained reads as discretion; an unexplained one reads as a bluff.
   */
  linksNote?: string;
};

export type StackItem = {
  name: string;
  note: string;
  usedIn: string[];
};

export type StackGroup = {
  id: string;
  title: string;
  caption: string;
  items: StackItem[];
};

export type Fact = {
  label: string;
  value: string;
};

/** One step of "how the work goes", shown on the contacts page. */
export type ProcessStep = {
  /** "01", "02", ... — rendered as the step marker. */
  number: string;
  title: string;
  body: string;
};

/**
 * A real quote from a real client. There is no placeholder variant on
 * purpose: an invented testimonial is the fastest way to lose a careful
 * reader, and the block simply does not render while the list is empty.
 */
export type Testimonial = {
  /**
   * The client's own words, verbatim — including the typos and the odd
   * grammar. Tidying a quote is editing evidence, and a polished testimonial
   * is the first thing a careful reader stops believing.
   */
  text: string;
  author: string;
  /**
   * What was actually delivered, in your words rather than the client's.
   * The quote may describe the order as the client understood it; this line
   * is a claim the site makes, so it must survive being checked.
   */
  role: string;
  /** ISO `YYYY-MM`. Rendered as month and year — a relative date would rot. */
  date?: string;
  /** Optional proof: a link to the review itself, or to the finished project. */
  href?: string;
};

export type TimelineEntry = {
  period: string;
  title: string;
  body: string;
};

/* ── Blog ─────────────────────────────────────────────────────────────────
   A post is a list of blocks rather than a string of HTML: the reader gets a
   fixed set of shapes that are already styled and already responsive, and a
   new post is written by picking from them instead of by writing markup.

   Every block is a plain object in `content.ts`, so adding a post never means
   touching a component. See the README for the whole catalogue with examples.
*/

/** A picture inside a post: a screenshot, a photo, a rendered diagram. */
export type BlogImage = {
  /** Built into /public/blog by tools/build_previews.py — use `img('name')`. */
  src: string;
  /** What the picture shows. A reader with images off still learns something. */
  alt: string;
  /** Printed under the picture in small type. Optional. */
  caption?: string;
};

/**
 * One bar of the `bars` infographic. `value` is what is drawn and what is
 * printed, so a chart can never disagree with its own label.
 */
export type BlogBar = {
  label: string;
  value: number;
  /** "мс", "%", "МБ" — appended to the printed number, not to the maths. */
  unit?: string;
  /** Marks the bar the post is arguing for: it is drawn in the accent colour. */
  highlight?: boolean;
};

/** One tile of the `stats` row: a number the post wants remembered. */
export type BlogStat = {
  value: string;
  label: string;
  /** One extra line under the label — where the number came from. */
  hint?: string;
};

export type BlogBlock =
  /** Body text. One paragraph per block. */
  | { type: 'text'; text: string }
  /** Section heading inside the article (`<h2>`). */
  | { type: 'heading'; text: string }
  /** Bulleted (default) or numbered list. */
  | { type: 'list'; items: string[]; ordered?: boolean }
  /** A picture. `wide` lets it break out of the text column. */
  | (BlogImage & { type: 'image'; wide?: boolean })
  /** Two or more pictures side by side, wrapping on narrow screens. */
  | { type: 'gallery'; images: BlogImage[] }
  /** A pulled-out sentence — the point of the section in one line. */
  | { type: 'quote'; text: string; author?: string }
  /** Source code. `lang` is a label only; nothing is highlighted. */
  | { type: 'code'; code: string; lang?: string; caption?: string }
  /** A boxed aside: a warning, a caveat, a result worth stopping on. */
  | { type: 'note'; tone?: 'info' | 'warn' | 'good'; title?: string; text: string }
  /** Infographic: a row of big numbers. */
  | { type: 'stats'; items: BlogStat[]; caption?: string }
  /** Infographic: a horizontal bar chart drawn in CSS, no library. */
  | { type: 'bars'; title?: string; items: BlogBar[]; caption?: string }
  /** Infographic: "было / стало" in two columns. */
  | {
      type: 'compare';
      before: { title: string; items: string[] };
      after: { title: string; items: string[] };
    };

export type BlogPost = {
  /** URL segment: `#/blog/preview-canvas`. Keep it stable once published. */
  id: string;
  title: string;
  /** The teaser in the feed, and the meta description of the open post. */
  excerpt: string;
  /** ISO `YYYY-MM-DD`. Absolute on purpose — a relative date rots. */
  date: string;
  /** Shown as chips in the feed. Two or three, not ten. */
  tags: string[];
  /** Feed thumbnail and the picture at the top of the post. */
  cover?: BlogImage;
  /** Overrides the estimate computed from the text. Usually leave it out. */
  readingMinutes?: number;
  body: BlogBlock[];
};
